/**
 * applyEffectPre — set ctx.filter for cheap effects that can ride along on
 * the main drawElementImage call. Anything more elaborate (shadow loops,
 * shader passes) is handled in applyEffectPost.
 *
 * Cheap effects:
 *  - blur:      ctx.filter = blur(Npx)
 *  - grayscale: ctx.filter = grayscale(N)
 *  - chromatic: cheap approximation via saturate + hue-rotate; the WebGL
 *               shader runs in applyEffectPost and overpaints the result, so
 *               this only matters in DOM fallback or shader-runtime failure.
 *  - crt:       cheap tone-curve approximation; same overpaint story.
 *
 * For glow/outline/frostedGlass, pre-pass is `none` because the post pass
 * is the real renderer.
 */
import type { Effect } from '../store/types';

export function applyEffectPre(ctx: CanvasRenderingContext2D, effect: Effect): void {
  ctx.filter = preFilter(effect);
}

/** Pure helper: the filter string applyEffectPre would set. Exposed for tests. */
export function preFilter(effect: Effect): string {
  switch (effect.kind) {
    case 'none':
      return 'none';
    case 'blur':
      return `blur(${Math.round(effect.px)}px)`;
    case 'grayscale':
      return `grayscale(${clamp01(effect.amt)})`;
    case 'chromatic': {
      // Pre-pass cheap approximation in case shader runtime is unavailable.
      const i = clamp01(effect.offset / 4);
      return `saturate(${1 + i * 2}) hue-rotate(${i * 30}deg)`;
    }
    case 'crt': {
      const i = clamp01(effect.intensity);
      return `contrast(${1 + i * 0.5}) saturate(${1 + i * 0.8}) brightness(${1 - i * 0.1})`;
    }
    case 'glow':
    case 'outline':
    case 'frostedGlass':
      return 'none';
  }
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}
